import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Loader2, Pencil, Plus, Target, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth, isAdmin } from "../../contexts/AuthContext";
import { indexLibrary, EMPTY_EVAL, isLegacy, overridesOf, autoLayer1, shotTypePlans } from "./evalModel";
import { EvalEditor } from "./EvalEditor";
import { EvalResults } from "./EvalResults";
import { LegacyResults } from "./LegacyResults";

// Shot Diet tab · 3-layer Shooting Assessment (Results -> Outcomes -> Constraints & Mechanics). Admin-only edits.
export default function ShootingEvaluation({ playerId, playerName, seasonAverages }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [lib, setLib] = useState(null);
  const [list, setList] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);

  const load = () => api.shootingEvaluations(playerId).then(rows => { setList(rows); setSelectedId(prev => (rows.some(r => r.assessment_id === prev) ? prev : rows[0]?.assessment_id || null)); }).catch(() => setList([]));
  useEffect(() => { api.shootingLibrary().then(setLib).catch(() => toast.error("Could not load the shooting library")); load(); }, [playerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const L = useMemo(() => (lib ? indexLibrary(lib) : null), [lib]);
  const current = list?.find(r => r.assessment_id === selectedId) || null;

  const startNew = () => { setDraft({ ...EMPTY_EVAL(), assessed_by: user?.name || "" }); setEditing("new"); setOpen(true); };
  const startEdit = () => {
    const base = EMPTY_EVAL();
    const { layer1, layer2, layer3, layer4 } = current;
    const l1 = layer1 ? { ...base.layer1, ...layer1, stats: { ...base.layer1.stats, ...layer1.stats } } : base.layer1;
    if (!Object.keys(l1.auto || {}).length) l1.auto = autoLayer1(l1.stats);
    setDraft({ ...base, date: current.date, assessed_by: current.assessed_by, notes: current.notes || "",
      layer1: l1,
      layer2: layer2 ? { ...base.layer2, ...layer2 } : base.layer2, layer3: layer3 ? { ...base.layer3, ...layer3 } : base.layer3, layer4: layer4 || base.layer4 });
    setEditing(current.assessment_id); setOpen(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...draft, layer1: { ...draft.layer1, shot_type_plans: shotTypePlans(draft.layer1.stats), overrides: overridesOf(draft.layer1) } };
      const saved = editing === "new" ? await api.createShootingEvaluation(playerId, body) : await api.updateShootingEvaluation(playerId, editing, body);
      toast.success("Shooting assessment saved");
      setEditing(null); setDraft(null);
      await load(); setSelectedId(saved.assessment_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setSaving(false); }
  };
  const remove = async () => {
    if (!window.confirm("Delete this shooting assessment?")) return;
    try { await api.deleteShootingEvaluation(playerId, current.assessment_id); toast.success("Deleted"); await load(); } catch { toast.error("Could not delete"); }
  };

  return (
    <section className="space-y-4" data-testid="shooting-evaluation">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <button type="button" onClick={() => setOpen(o => !o)} className="flex items-start gap-2 text-left group" aria-expanded={open} data-testid="se-toggle">
          {open ? <ChevronDown className="w-4 h-4 text-[#a0a0a0] group-hover:text-white shrink-0 mt-5" /> : <ChevronRight className="w-4 h-4 text-[#a0a0a0] group-hover:text-white shrink-0 mt-5" />}
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--org-accent)] flex items-center gap-2"><Target className="w-3.5 h-3.5" /> Shooting Assessment · {lib?.version || "Shooting"}</div>
            <div className="font-heading text-2xl uppercase tracking-tight text-white">{playerName || "Player"}'s Shooting Assessment</div>
            <div className="text-[10px] uppercase tracking-widest text-[#757575] mt-0.5">{open ? "Results · Film / Training · Mechanics & Constraints · Routines — there isn't one way to shoot" : `${list?.length || 0} ${list?.length === 1 ? "assessment" : "assessments"} · click to expand`}</div>
          </div>
        </button>
        {open && (
          <div className="flex items-center gap-2 flex-wrap">
            {!editing && list?.length > 1 && (
              <select value={selectedId || ""} onChange={e => setSelectedId(e.target.value)} className="bg-[#0f0f0f] border border-white/10 text-xs text-white px-3 py-2 focus:outline-none" data-testid="se-history">
                {list.map(r => <option key={r.assessment_id} value={r.assessment_id}>{new Date(r.date).toLocaleDateString()} · {r.assessed_by}</option>)}
              </select>
            )}
            {admin && current && !editing && <>
              <button onClick={startEdit} className="px-3 py-2 border border-white/10 text-[#a0a0a0] hover:text-white text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-1.5" data-testid="se-edit-btn"><Pencil className="w-3 h-3" /> Edit</button>
              <button onClick={remove} className="px-3 py-2 border border-white/10 text-[#a0a0a0] hover:text-[#f87171] text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-1.5" data-testid="se-delete-btn"><Trash2 className="w-3 h-3" /></button>
            </>}
            {admin && !editing && <button onClick={startNew} className="px-3 py-2 border border-[var(--org-accent)] text-[var(--org-accent)] text-[10px] uppercase tracking-[0.3em] font-bold flex items-center gap-1.5" data-testid="se-new-btn"><Plus className="w-3 h-3" /> New assessment</button>}
          </div>
        )}
      </div>

      {open && (!L || list === null ? (
        <div className="flex items-center gap-2 text-[#a0a0a0] text-sm py-6"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : editing ? (
        <EvalEditor L={L} ev={draft} setEv={setDraft} onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} saving={saving} playerId={playerId} gamesPlayed={seasonAverages?.games_played} />
      ) : current ? (
        isLegacy(current) ? <LegacyResults L={L} ev={current} playerId={playerId} /> : <EvalResults L={L} ev={current} playerId={playerId} />
      ) : (
        <div className="border border-dashed border-white/10 p-6 text-sm text-[#757575]" data-testid="se-empty">{admin ? "No shooting assessment yet — run the first one." : "No shooting assessment yet."}</div>
      ))}
    </section>
  );
}
